import { Component, OnInit, Input, ViewChild, HostListener, OnDestroy } from '@angular/core';
import { ResourceService } from 'sce-core';
import * as moment from 'moment';
import { DetailViewKeyEventHandlerService } from '../../../services/detail-view-key-event-handler.service';
import { SearchableEntityDataService } from '../../../services/searchable-entity/searchable-entity-data.service';
import { SearchableEntityDataEditorService } from '../../../services/searchable-entity/searchable-entity-data-editor.service';
import { NavigationService } from '../../../../core/navigation.service';
import { LeftPanelService } from '../../../../layout/home/services/left-panel.service';
import { AlertService } from 'sce-core';
import { UserService } from '../../../../core/security/user.service';
import { LFDynamicWidgetConfig } from '../../../models/lf-dynamic-widget-config';
import { FormPageConfig } from '../../../models/form-page-config';
import { DynamicWidget } from '../dynamic-widget';
import { PropertyChangeEvent, MANUAL_EDIT } from '../../../models/property-change-event';
import { DatePickerConfigService } from '../../datepicker/datepicker-config.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lfwms-abstract-form-widget',
  templateUrl: './abstract-form-widget.component.html',
  styleUrls: ['./abstract-form-widget.component.css']
})
export class AbstractFormWidgetComponent implements OnInit, OnDestroy, DynamicWidget {

  @Input() pageConfig: FormPageConfig;
  @Input() widgetConfig: LFDynamicWidgetConfig;

  public metadataMap: any;
  public filteredMetaData: any;
  public cellDataMap: any;
  public propertyList: any;
  public accordionTitle: string;
  public isLoading = false;
  public isPrimaryDataLoaded = false;
  public panel: any;
  public default: any;
  public dataService: SearchableEntityDataService;
  public dataEditor: SearchableEntityDataEditorService;
  public keyEventHandler: DetailViewKeyEventHandlerService;
  public windowWidth: any;
  public sidePanelWidth: number;
  public searchValue: any;
  public originalCellDataMap: any; // stores the original data
  public dateFormat: string;
  public dateTimeFormat: string;
  private eventSubscriptions: Subscription = new Subscription();

  // Method to recalculate cell width and align Property Grid on Window Resize event
  @HostListener('window:resize', ['$event'])
  public onResize($event: any) {
    this.windowWidth = $event.target.innerWidth;
    this.realignWidget();
  }

  constructor(protected resourceService: ResourceService,
    protected navService: NavigationService,
    protected leftPanelService: LeftPanelService,
    protected alertService: AlertService,
    protected userService: UserService,
    protected dateConfigService: DatePickerConfigService) {
  }

  public ngOnInit() {
    this.initializeWidget();
    this.dateFormat = this.dateConfigService.defaultDateDisplayFormat;
    this.dateTimeFormat = this.dateConfigService.defaultDateDisplayFormat + ' ' + this.dateConfigService.defaultTimeDisplayFormat;
  }

  protected initializeWidget() {
    this.dataService = this.pageConfig.dataService;
    this.dataEditor = this.pageConfig.dataEditor;
    this.keyEventHandler = this.pageConfig.keyEventHandler;
    this.metadataMap = this.pageConfig.metaDataMap;
    // LFWM-2771 - trim the trailing space from dropdown values
    this.overrideMetadataMap(this.metadataMap);
    this.filteredMetaData = JSON.parse(JSON.stringify(this.metadataMap));
    this.cellDataMap = this.pageConfig.cellDataMap;
    this.originalCellDataMap = JSON.parse(JSON.stringify(this.cellDataMap));
    // LFWM-2971- initializing value for originalDataMap.
    this.dataService.originalDataMap = JSON.parse(JSON.stringify(this.cellDataMap));
    /* set initial value before first call to realignPropertyGrid()
    or  any events are received via subscription.edge case. */
    this.sidePanelWidth = this.leftPanelService.isOpened() ? this.leftPanelService.getLeftPanelWidthInPixels() : 0;
    this.realignWidget();
    this.leftPanelService.getLeftPanelOpenEvent().subscribe((isOpened) => {
      if (isOpened) {
        this.sidePanelWidth = this.leftPanelService.getLeftPanelWidthInPixels();
      } else {
        this.sidePanelWidth = 0;
      }
      this.realignWidget();
    });
    this.accordionTitle = this.widgetConfig.name;
    if (this.cellDataMap['storerKey'] || this.cellDataMap['fromStorerKey']) {
      /* [LFWM-2923] - to filter all the dropdown values when changing the storer value in accordion and table
      when loading the page itself */
      this.dataEditor.populateDropdownValuesBasedOnStorerInAccordion();
      this.dataEditor.populateDropdownValuesBasedOnStorerInTables();
    }else {
      /*[LFWM - 2923] - To filter the dropdown values based on filter
      which don't have StorerKey in meta data
      From User info fetch the storer restictions if any and do the filtering */
      const userRestrictions = this.userService && this.userService.userInfo && this.userService.userInfo.userRestrictions; 
      if (userRestrictions && userRestrictions[0]) {
        const storerRestriction = this.userService.userInfo.userRestrictions[0]['restrictions']
          .find(x => x.restrictionTypeName === 'Storer');
        if (storerRestriction && storerRestriction.restrictionValue != null) {
          this.dataEditor.populateDropdownValuesBasedOnDefaultStorer(storerRestriction.restrictionValue.split(','));
        }
      }
    }
    this.subscriptionFunction();
  }
  // LFWM-2771 - trim the trailing space from dropdown values -Method to Customize Metadata for Display Only Changes
  private overrideMetadataMap(cMetadataMap: any) {
    // LFWM-2709 Overriding Metadata for dropdowns to display it in ['Key - Value'] format
    for (const column in cMetadataMap) {
      if ((cMetadataMap[column].type === 'DROPDOWN') || (cMetadataMap[column].type === 'EDITABLEDROPDOWN')) {
        /* assigning the dropdown options to orginalOptions. when we are loading the page first time we are taking the dropdown options
        from metadata. */
        cMetadataMap[column].orginalOptions = cMetadataMap[column].orginalOptions ? cMetadataMap[column].orginalOptions :
          cMetadataMap[column].values;
        if (cMetadataMap[column].orginalOptions) {
          /* Below, Deep Clone has to be performed to avoid directly updating Metadata Object in Service
           replacing the column dropdown values by column originol options to avoid overriding after the first time */
          cMetadataMap[column].values = JSON.parse(JSON.stringify(cMetadataMap[column].orginalOptions));
        }
        cMetadataMap[column].values.forEach((option: any) => {
          if (option.key) {
            option.key = option.key.trim();
          }
        });
      }
    }
  }
  // realigns the widget
  public realignWidget() {
    // this should be implemented separately for every type of widget
  }

  // method used to translate the labels
  public getLabel(key: any) {
    return this.resourceService.get(key);
  }

  // Method to return Cell Title corresponding to each property
  public getCellTitle(property: any): string {
    return this.resourceService.get(this.metadataMap[property].elementId);
  }

  // Method  to override default display format for each cell value (in VIEW Mode ONLY)
  public getCellDisplayValue(property: any): any {
    if (!this.isLoading) {
      let cellDisplayValue: any;
      const cellDefinition: any = this.cellDataMap[property];
      // Below, Deep Clone has to be performed to avoid directly updating Metadata Object in Service
      const cellMetadata: any = JSON.parse(JSON.stringify(this.metadataMap[property]));
      // in dropdown field if default option of input options [- Select -] is selected then the cellDisplayValue should be empty in view mode.
      if (cellDefinition && !cellDefinition.cVal) {
        // if the qty field is empty then defaultvalue from metadata should taken
        if (property === 'qty' && cellMetadata.defaultVal !== '') {
          cellDisplayValue = cellMetadata.defaultVal;
        } else {
          cellDisplayValue = '';
        }
      } else {
        if (cellMetadata.type === 'DROPDOWN' || cellMetadata.type === 'EDITABLEDROPDOWN') {
          // to handle dropdown fields with cOptions
          /* [LFWM-2447] - dropdown values should be filtered based on storerKey
          before comparing with cVal of corresponding field from loadbypage */
          if (cellMetadata.type === 'DROPDOWN' && this.dataService.displayMode === 'V') {
            cellMetadata.values = this.filterMetaData(property);
          }
          const filteredValueObject = cellMetadata.values.filter((valueObject) => {
            // For string type dropdowns case insensitive comparison is done
            if (valueObject.key && cellDefinition.cVal && typeof (valueObject.key) === 'string' && typeof (cellDefinition.cVal) === 'string') {
              return (valueObject.key.toUpperCase() === cellDefinition.cVal.toUpperCase());
            } else {
              return (valueObject.key === cellDefinition.cVal);
            }
          });
          if (filteredValueObject.length > 0) {
            // LFWM-2709 - display only key in view mode
            //LFWM-3357- display value of dropdown in view mode
            cellDisplayValue = filteredValueObject[0].value;
          }  else {
            /* [LFWM-2114] -if type - EDITABLEDROPDOWN - In View mode the input value of dropdown is not present in dropdown list then the input value
            from loadbypage is assigned to cellDisplayValue */
            // If cell value not in dropdown show that value as such
            cellDisplayValue = cellDefinition.cVal;
          }
        } else if (cellMetadata.type === 'DATE' || cellMetadata.type === 'DATETIME') {
          if (cellDefinition.cVal) {
            //  cellDisplayValue = moment(cellDefinition.cVal).format('MM/DD/YYYY');
            if (cellMetadata.type === 'DATE') {
              cellDisplayValue = this.dateConfigService.getFormattedDisplayDate(cellDefinition.cVal);
            } else if (cellMetadata.type === 'DATETIME') {
              cellDisplayValue = this.dateConfigService.getFormattedDisplayDateTime(cellDefinition.cVal);
            }
          } else {
            cellDisplayValue = '';
          }
        } else {
          cellDisplayValue = cellDefinition.cVal;
        }
      }
      return cellDisplayValue;
    } else {
      return '';
    }
  }

  public openLookup(cells: any, property: any) {
    const row = { cells: this.cellDataMap };
    this.dataEditor.openLookup(row, property);
  }

  public valueChanged(key: any, sourceEvent?: any) {
    let newVal = null;
    if (sourceEvent !== null && sourceEvent !== undefined) {
      if ('string' === typeof sourceEvent) {
        newVal = sourceEvent;
      } else if ('object' === typeof sourceEvent) {
        newVal = sourceEvent['newVal'];
      }
    }

    if ((newVal === null || newVal === undefined) && this.metadataMap[key].type !== 'DATE' && this.metadataMap[key].type !== 'DATETIME') {
      newVal = this.cellDataMap[key].cVal;
    }

    //  this.cellDataMap[key].cValPrev = this.originalCellDataMap[key].cVal; // cVal prev is obtained from the original data
    // if the value has changed, send an event and check if the change can be accepted
    const event: PropertyChangeEvent = new PropertyChangeEvent();
    event.source = 'table-editor';
    event.type = MANUAL_EDIT;
    event.property = this.cellDataMap[key].cName;
    event.value = newVal;
    // JIRA LFWM-1474 Fixes - current value and previous value of a dropdown element should not be the same value
    if (sourceEvent && sourceEvent.type && sourceEvent.type === 'dropdown') {
      event.previousValue = sourceEvent.prevVal;
    } else {
      event.previousValue = this.cellDataMap[key].cVal;
    }
    const acceptChange: boolean = this.dataEditor.propertyValueChanged(event);
    // if the property change is to be rejected, do not set the is edited flag
    if (acceptChange) {
      // LFWM-2796 - compare new value and original value and set isEdited
      // LFWM-2951- comparing the new val with the originalDataMap of dataService.(this.originalCellDataMap doesnot get updated while editing)
      let hasValueChanged: boolean;
      let prevVal: any;
      if (this.dataService.originalDataMap[key]) {
        hasValueChanged = (newVal !== this.dataService.originalDataMap[key].cVal);
        prevVal = this.dataService.originalDataMap[key].cVal;
      } else if (this.originalCellDataMap[key]) {
        hasValueChanged = (newVal !== this.originalCellDataMap[key].cVal);
        prevVal = this.originalCellDataMap[key].cVal;
      }
      this.cellDataMap[key].isEdited = hasValueChanged;
      this.navService.preventNavigation = hasValueChanged;
      // if the event is from dropdown the current value is already set in this.cellDataMap[key].cVal
      if (sourceEvent && sourceEvent.type && sourceEvent.type === 'dropdown') {
        this.cellDataMap[key].cValPrev = sourceEvent.prevVal;
      } else {
        this.cellDataMap[key].cValPrev = prevVal;
        this.cellDataMap[key].cVal = newVal;
      }
    }
    // [LFWM-2923] - filtering the dropdown values based on storer when storer key value changed
    if (key === 'storerKey' || key === 'fromStorerKey') {
      this.dataEditor.populateDropdownValuesBasedOnStorerInAccordion();
      this.dataEditor.populateDropdownValuesBasedOnStorerInTables();
    }
  }
  // when value change in type ahead suggestion component
  public onValueChangeInTypeaheadTextBox(event) {
    this.valueChanged(event.propertyName, event);
  }

  // Method for checkbox starts
  public toggleSelect(key) {
    this.cellDataMap[key].isEdited = true;
    this.cellDataMap[key].cValPrev = this.cellDataMap[key].cVal;
    if (this.cellDataMap[key].cVal === 'Y' || this.cellDataMap[key].cVal === '1') {
      // If datatype of checkbox value is string
      if (this.metadataMap[key].dataType === 'String') {
        this.cellDataMap[key].cVal = 'N';
      } else if (this.metadataMap[key].dataType === 'Integer') { // If datatype of checkbox value is string
        this.cellDataMap[key].cVal = '0';
      }
    } else {
      if (this.metadataMap[key].dataType === 'String') {
        this.cellDataMap[key].cVal = 'Y';
      } else if (this.metadataMap[key].dataType === 'Integer') {
        this.cellDataMap[key].cVal = '1';
      }
    }
  }
  // Method for checkbox ends

  // [LFMW-2447] - method used to filter the dropdown values based on storerkey
  public filterMetaData(key: any) {
    let storerObj;
    const tableId = this.dataService.entityConfig.getPrimarySearchParams().tableId;
    // LFWM-2951-In transfer screen and channelTransfer screen dropdown values should be filtered based on fromStorerKey
    //  before comparing with cVal of corresponding field from loadbypage
    if (tableId === 'transfertd' || tableId === 'channeltransfertd') {
      storerObj = this.cellDataMap['fromStorerKey'];
    } else {
      storerObj = this.cellDataMap['storerKey'];
    }
    // Getting storer restrictions.
    let storers = [];
    const userRestrictions = this.userService && this.userService.userInfo && this.userService.userInfo.userRestrictions;
    if (userRestrictions && userRestrictions[0]) {
      const storerRestriction = this.userService.userInfo.userRestrictions[0]['restrictions']
        .find(x => x.restrictionTypeName === 'Storer');
      if (storerRestriction && storerRestriction.restrictionValue != null) {
        storers = storerRestriction.restrictionValue.split(',').map(x => x.trim().toUpperCase());
      }
    }
    if (storerObj && storerObj.cVal) {
        // If no restrictions, use storerKey value. else filter in restrictions.
      if (storers.length === 0) {
        storers = [storerObj.cVal.toUpperCase()];
      } else {
        storers = storers.filter(storer => storer === storerObj.cVal.toUpperCase());
      }
    }
    this.filteredMetaData[key].values = this.dataEditor.getValuesBasedOnDefaultStorer(this.metadataMap[key].values, storers);
    return this.filteredMetaData[key].values;
  }

  public subscriptionFunction() {
    this.eventSubscriptions.add(this.dataService.valueChangedReceiveEvent().subscribe((event) => {
      if (event.property === 'storerKey' || event.property === 'fromStorerKey') {
        this.dataEditor.populateDropdownValuesBasedOnStorerInAccordion();
        this.dataEditor.populateDropdownValuesBasedOnStorerInTables();
      }
    }));
  }

  public ngOnDestroy() {
    if (this.eventSubscriptions) {
      this.eventSubscriptions.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/shared/components/widget/abstract-form-widget/abstract-form-widget.component.ts